import React from 'react';
import qs from 'qs';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { PLAYER_URL, BUILD_ENVIRONMENT } from 'Settings';
import * as usersActions from 'Store/users/actions';
import * as reducers from 'Store/reducers';
import * as analyticsActions from 'Store/stats/actions';
import * as amplitudeEvents from 'Store/stats/amplitude';

import styles from './GamePlayer.scss';
import GameContext from 'Containers/GameContext';
import GameCanvas from 'Components/GameCanvas';
import GamePlayerControls from 'Components/GamePlayerControls';
import GameDrawer from 'Components/GameDrawer';
import GameLoading from 'Components/GameLoading';
import { captureMessage } from 'Utils/errorTracking';

class GamePlayer extends React.Component {
  constructor(props) {
    super(props);

    const gameSettings = {
      languageCodes: props.supportedLanguages,
      currentLanguage: 'en',
      labels: true,
      audio: true,
      fullscreen: false,
    };

    this.state = {
      gameSettings,
      iframeInitialized: false,
      loaderInitialized: false,
      gameLoaded: false,
      loadingProgress: 0,
      loadingStart: null,
      loadingComplete: null,
      gameUrisDelivered: false,
      gameLoadingError: false,
      analyticsInitialized: false,
      sentGamePlayedEvent: false,
      showAppLinks: false,
      activeScene: 'Scene_Dashboard',
    };

    this.receiveMessage = this.receiveMessage.bind(this);
    this.postMessage = this.postMessage.bind(this);
    this.sendGameUris = this.sendGameUris.bind(this);
    this.updateSettings = this.updateSettings.bind(this);
    this.toggleSetting = this.toggleSetting.bind(this);
    this.changeSettingValue = this.changeSettingValue.bind(this);
    this.initializeAnalytics = this.initializeAnalytics.bind(this);
    this.logAnalyticsEvent = this.logAnalyticsEvent.bind(this);
    this.toggleFullScreen = this.toggleFullScreen.bind(this);
    this.updateCanvasSize = this.updateCanvasSize.bind(this);
    this.reloadGame = this.reloadGame.bind(this);
    this.offlineCheckTimeout = null;
  }

  componentDidMount() {
    window.addEventListener('message', this.receiveMessage, false);
    document.addEventListener('fullscreenchange', this.updateCanvasSize, false);
    document.addEventListener(
      'mozfullscreenchange',
      this.updateCanvasSize,
      false
    );
    document.addEventListener(
      'webkitfullscreenchange',
      this.updateCanvasSize,
      false
    );
    document.addEventListener(
      'msfullscreenchange',
      this.updateCanvasSize,
      false
    );
    this.offlineCheckTimeout = window.setInterval(() => {
      const { gameLoaded, gameLoadingError, gameLoadingErrorMessageId } =
        this.state;
      const msgId = 'errors.model_network_load';
      if (!navigator.onLine && !gameLoaded && !gameLoadingError)
        this.setState({
          gameLoadingError: true,
          gameLoadingErrorMessageId: msgId,
        });
      if (navigator.onLine && gameLoadingErrorMessageId === msgId)
        this.reloadGame();
    }, 1000);

    this.setState({ loadingStart: Date.now() });
  }

  componentDidUpdate(prevProps, prevState) {
    this.sendGameUrisIfNeeded();
    this.sendGameLoadingTimeIfNeeded();

    /* NOTE: This code attempts to disable app on mobile devices -- disabled for now */

    // if (!this.state.gameLoadingError) {
    //   let msgId = null;
    //   if (typeof window.orientation !== 'undefined')
    //     msgId = 'errors.model_device';
    //   if (window.innerWidth < 720 && !msgId) msgId = 'errors.model_screen_size';

    //   if (msgId !== null)
    //     this.setState({
    //       gameLoadingError: true,
    //       gameLoadingErrorMessageId: msgId,
    //       showAppLinks: msgId === 'errors.model_device',
    //     });
    // }

    if (
      this.state.activeScene &&
      this.state.activeScene !== prevState.activeScene
    ) {
      this.props.sendAnalyticsEventById('change_a_scene', null, {
        app: this.props.slug,
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.receiveMessage);
    document.removeEventListener('fullscreenchange', this.updateCanvasSize);
    document.removeEventListener('mozfullscreenchange', this.updateCanvasSize);
    document.removeEventListener(
      'webkitfullscreenchange',
      this.updateCanvasSize
    );
    document.removeEventListener('msfullscreenchange', this.updateCanvasSize);
    window.clearInterval(this.offlineCheckTimeout);
  }

  sendGameLoadingTimeIfNeeded() {
    const { loadingStart, loadingComplete } = this.state;
    if (loadingStart && loadingComplete) {
      this.setState(
        {
          loadingStart: null,
          loadingComplete: null,
        },
        () =>
          this.props.sendGameLoadingTime(
            this.props,
            loadingStart,
            loadingComplete
          )
      );
    }
  }

  sendGameUrisIfNeeded() {
    const { location } = this.props;
    const { load } = qs.parse(location.search, {
      ignoreQueryPrefix: true,
    });
    const stopLoader = load === 'false';
    if (
      !stopLoader &&
      this.iframeElement &&
      this.state.iframeInitialized &&
      this.props.gameSettings.loader &&
      !this.state.gameUrisDelivered &&
      !this.state.loaderInitialized
    ) {
      this.setState(
        {
          gameUrisDelivered: true,
        },
        () => this.sendGameUris()
      );
    }
  }

  reloadGame() {
    this.setState({
      iframeInitialized: false,
      loaderInitialized: false,
      gameLoaded: false,
      loadingProgress: 0,
      loadingStart: Date.now(),
      loadingComplete: null,
      gameLoadingError: false,
      gameLoadingErrorMessageId: null,
      gameUrisDelivered: false,
      analyticsInitialized: false,
    });
  }

  initializeAnalytics() {
    const { analyticsInitialized } = this.state;
    const { slug, activeUser } = this.props;

    if (!analyticsInitialized && activeUser) {
      const amplitudeInstance = amplitudeEvents.initializeAmplitude(
        slug,
        activeUser
      );
      if (amplitudeInstance) this.setState({ analyticsInitialized: true });
    }
  }

  logAnalyticsEvent(event) {
    const { slug } = this.props;
    if (!this.state.sentGamePlayedEvent) {
      this.setState({ sentGamePlayedEvent: true }, () => {
        this.props.sendAnalyticsEventById('play_a_model', null, {
          app: slug,
        });
      });
    }
    amplitudeEvents.logGameEvent(slug, event.data.analytics);
  }

  toggleFullScreen() {
    const el = this.gamePlayerElement;
    const { fullscreen } = this.state.gameSettings;
    if (el.requestFullscreen) {
      if (fullscreen) {
        document.exitFullscreen();
      } else {
        el.requestFullscreen();
      }
    } else if (el.mozRequestFullScreen) {
      if (fullscreen) {
        document.mozCancelFullScreen();
      } else {
        el.mozRequestFullScreen();
      }
    } else if (el.webkitRequestFullScreen) {
      if (fullscreen) {
        document.webkitExitFullscreen();
      } else {
        el.webkitRequestFullScreen();
      }
    } else if (el.msRequestFullScreen) {
      if (fullscreen) {
        el.msExitFullscreen();
      } else {
        el.msRequestFullScreen();
      }
    }
  }

  updateCanvasSize() {
    const {
      fullscreen,
      mozFullScreen,
      webkitIsFullScreen,
      msFullscreenElement,
    } = document;
    const isFullscreen =
      fullscreen || mozFullScreen || webkitIsFullScreen || msFullscreenElement;
    this.setState((prevState) => ({
      gameSettings: {
        ...prevState.gameSettings,
        fullscreen: typeof isFullscreen === 'boolean' ? isFullscreen : false,
      },
    }));
    // Wow, I did not document how I got to this number. Oof.
    const aspectRatio = 1.65887;
    let fullScreenWidth;
    let fullScreenHeight;
    if (window.screen.width / window.screen.height > aspectRatio) {
      fullScreenWidth = ((window.screen.height - 40) * 4) / 3;
      fullScreenHeight = (fullScreenWidth * 3) / 4;
      this.gamePlayerElement.style.width = isFullscreen
        ? `${(fullScreenWidth * 4) / 3}px`
        : '100%';
    } else {
      fullScreenWidth = (window.screen.width * 3) / 4;
      fullScreenHeight = (fullScreenWidth * 3) / 4;
      this.gamePlayerElement.style.height = isFullscreen
        ? `${fullScreenHeight + 40}px`
        : '100%';
    }

    this.postMessage({
      newDimensions: {
        width: isFullscreen ? fullScreenWidth : 720,
        height: isFullscreen ? fullScreenHeight : 540,
      },
    });
  }

  toggleSetting(settingKey) {
    this.changeSettingValue(settingKey, !this.state.gameSettings[settingKey]);
  }

  changeSettingValue(settingKey, value) {
    const changes = {};
    const newSettings = this.state.gameSettings;
    newSettings[settingKey] = value;
    changes[settingKey] = newSettings[settingKey];
    this.updateSettings(changes, newSettings);
    amplitudeEvents.logGameSettingsChange(this.props.slug, {
      name: settingKey,
      value,
    });
  }

  updateSettings(changes, newSettings) {
    const stringifySettings = () => {
      return JSON.stringify(changes)
        .replace(/true/gi, '1')
        .replace(/false/gi, '0');
    };
    this.setState({
      gameSettings: newSettings,
    });
    this.postMessage({
      changedSettings: stringifySettings(),
    });
  }

  receiveMessage(event) {
    // For Chrome, the origin property is in the event.originalEvent object.
    const origin = event.origin || event.originalEvent.origin;
    if (origin !== PLAYER_URL) return;

    if (typeof event.data === 'object') {
      if (event.data.loadingProgress) {
        this.setState({ loadingProgress: event.data.loadingProgress });
      }
      if (event.data.gameLoaded) {
        const { token, slug, activeUser, initialScene, loadingFinishedEvent } =
          this.props;
        this.setState({
          gameLoaded: loadingFinishedEvent === 'gameLoaded',
          loadingComplete: Date.now(),
        });
        const scene = initialScene || {};
        const initMessage = {
          token,
          ...scene,
          userId: activeUser ? activeUser.id : undefined,
          environment: BUILD_ENVIRONMENT || 'default',
        };
        this.postMessage(initMessage);
        this.updateSettings(this.state.gameSettings, this.state.gameSettings);
        if (event.data.appVersionNumber) {
          amplitudeEvents.setGameVersionNumber(
            slug,
            event.data.appVersionNumber
          );
        }
      }
      if (event.data.gameInitialized) {
        this.setState({
          gameLoaded: this.props.loadingFinishedEvent === 'gameInitialized',
          loadingComplete: Date.now(),
          activeScene: event.data.sceneName,
        });
      }
      if (event.data.iframeInitialized) {
        this.setState({ iframeInitialized: true });
      }

      if (event.data.loaderInitialized) {
        this.initializeAnalytics();
        // console.log(
        //   'indexedDb readyState: ',
        //   window.indexedDB.open('UnityCache', 2).readyState
        // );
        this.setState({ loaderInitialized: true });
      }

      if (event.data.retryInitialization) {
        window.setTimeout(this.sendGameUris, 500);
      }

      if (event.data.activeScene) {
        const { activeScene } = event.data;
        this.setState({ activeScene });
      }
      if (event.data.analytics) {
        this.logAnalyticsEvent(event);
      }
      if (event.data.error) {
        this.setState({ gameLoadingError: true });
        const { error } = event.data;
        const baseSentryObj = {
          level: 'error',
          tags: {
            model: this.props.slug,
          },
          extra: {
            ...error.extra,
            unityVersion: this.props.gameSettings.unityVersion,
          },
        };

        if (error.type === 'loading') {
          console.log('unityloader.js error');
          console.log(error);
          captureMessage('UnityLoader.js could not be loaded', baseSentryObj);
          captureMessage('UnityLoader.js could not be loaded... error:', error);
        }
        if (error.type === 'unity') {
          captureMessage('Unity script error', baseSentryObj);
        }
      }
    }
  }

  postMessage(msg) {
    this.iframeElement.contentWindow.postMessage(msg, PLAYER_URL);
  }

  sendGameUris() {
    this.postMessage({
      gameSettings: this.props.gameSettings,
    });
  }

  render() {
    const { loaderInitialized, gameLoaded, activeScene, gameSettings } =
      this.state;
    const { location } = this.props;
    const { target } = qs.parse(location.search, {
      ignoreQueryPrefix: true,
    });
    const fullscreenSupported = !(target && target !== 'schools');

    return (
      <div
        id="player"
        className={`GamePlayer GamePlayer--${this.props.slug}`}
        ref={(div) => (this.gamePlayerElement = div)}
      >
        <div className="GamePlayer__container">
          <GameLoading
            {...this.state}
            {...this.props}
            handleReload={() => this.reloadGame()}
          />
          <div
            className="GamePlayer__canvas-group"
            data-initialized={loaderInitialized || 'false'}
          >
            <GameCanvas
              src={`${PLAYER_URL}/assets/games/${this.props.gameSettings.unityVersion}/index.html`}
              iframeRef={(el) => (this.iframeElement = el)}
              gameLoadingError={this.props.gameLoadingError}
            />
            <GamePlayerControls
              gameLoaded={gameLoaded}
              gameSettings={gameSettings}
              fullscreenSupported={fullscreenSupported}
              onAudioToggle={() => this.toggleSetting('audio')}
              onLabelToggle={() => this.toggleSetting('labels')}
              onFullScreenToggle={() => this.toggleFullScreen()}
              onLanguageSelect={(e) =>
                this.changeSettingValue('currentLanguage', e.target.value)
              }
            />
          </div>
          <GameDrawer
            {...this.props}
            loaderInitialized={loaderInitialized || 'false'}
            gameLoaded={gameLoaded}
            activeScene={activeScene}
          />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    token: reducers.getAuthToken(state),
    activeUser: reducers.getActiveUser(state),
  };
};

export default GameContext(
  connect(mapStateToProps, {
    ...usersActions,
    ...analyticsActions,
  })(withRouter(GamePlayer))
);
